import React, { createContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signInWithCredential, signOut } from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, googleProvider, db, GOOGLE_ONE_TAP_CLIENT_ID } from '../firebase';

export const AuthContext = createContext();

const oneTapInitialized = { current: false };

function tryShowOneTap(onCredential) {
    if (!GOOGLE_ONE_TAP_CLIENT_ID || typeof window === 'undefined' || !window.google?.accounts?.id) return false;
    try {
        if (!oneTapInitialized.current) {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_ONE_TAP_CLIENT_ID,
                callback: (response) => {
                    if (response?.credential) onCredential(response.credential);
                },
                cancel_on_tap_outside: true,
            });
            oneTapInitialized.current = true;
        }
        window.google.accounts.id.prompt();
        return true;
    } catch (e) {
        return false;
    }
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [plan, setPlan] = useState('free');
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            if (!firebaseUser) {
                setPlan('free');
                setSubscription(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Google One Tap: mostrar el prompt nativo de Google al detectar que no está logueado (estándar de industria)
    useEffect(() => {
        if (loading || user) return;
        const t = setTimeout(() => {
            tryShowOneTap((idToken) => {
                const credential = GoogleAuthProvider.credential(idToken);
                signInWithCredential(auth, credential).catch(() => {});
            });
        }, 1200);
        return () => clearTimeout(t);
    }, [loading, user]);

    // Escuchar perfil del usuario en Firestore para saber si es premium
    useEffect(() => {
        if (!user) return;
        const userRef = doc(db, 'users', user.uid);
        const unsubscribe = onSnapshot(
            userRef,
            (snap) => {
                const data = snap.data() || {};
                const isPremium = !!data.isPremium;
                setPlan(isPremium ? 'premium' : 'free');
                setSubscription(data.subscription || null);
            },
            () => {}
        );
        return () => unsubscribe();
    }, [user]);

    const signInWithGoogle = async () => {
        try {
            // Siempre usar popup al hacer clic: así funciona aunque FedCM esté desactivado en el navegador.
            // One Tap solo se muestra automáticamente al cargar la página (efecto más arriba).
            await signInWithPopup(auth, googleProvider);
        } catch (err) {
            console.error('Error al iniciar sesión:', err?.message || err);
            if (err?.code === 'auth/popup-blocked') {
                alert('El navegador bloqueó la ventana de inicio de sesión. Permití ventanas emergentes para este sitio.');
            } else if (err?.code === 'auth/unauthorized-domain') {
                alert('Este dominio no está autorizado. Añadí privilead.com en Firebase (Authorized domains) y en Google Cloud (Orígenes de JavaScript autorizados).');
            }
        }
    };

    const signOutUser = async () => {
        await signOut(auth);
        try {
            if (typeof window !== 'undefined' && window.google?.accounts?.id) {
                window.google.accounts.id.disableAutoSelect?.();
            }
        } catch (_) {}
    };

    return (
        <AuthContext.Provider value={{ user, loading, plan, subscription, signInWithGoogle, signOutUser }}>
            {children}
        </AuthContext.Provider>
    );
}
